import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  MuiThemeProvider,
  createMuiTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import { useHistory, useParams } from "react-router-dom";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import Table, { buttonsStyle, dateFormatter } from "../../../components/tables/table";
import { getBacktestingResultById, deleteBacktestingResult } from "../../../../api/backtestingResult";
import { Col, Row } from "react-bootstrap";
import Plot from 'react-plotly.js';
import EditBacktestingConfigPageInfo from "../backtestingConfigs/EditBacktestingConfigPageInfo";

const fuxiaColor = "#cc02d6"

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    primary: {
      main: fuxiaColor,
    },
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyBacktestingResults() {
  return {
    fullName: "",
    isMain: false,
    interval: 120,
    emaPeriod: 3,
    filterTop: 0,
    filterBottom: -2.5,
    percentageComission: 0.045,
    longs: true,
    shorts: true,
    source: "close",
    active: true,
  };
}

function sortByProfitFactor(a, b) {
  if (a.profitFactor > b.profitFactor) return -1;
  if (a.profitFactor < b.profitFactor) return 1;
  return 0;
}

export default function EditBacktestingResultsPage() {
  const [backtestingResults, setBacktestingResults] = useState(getEmptyBacktestingResults());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [visibleSymbol, setVisibleSymbol] = useState(null);
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const backtestingResultsId = useParams().id;
  const history = useHistory();

  const [refresh, setRefresh] = useState(false);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    setRefresh(false);
  }, [refresh])

  useEffect(() => {
    if (!backtestingResultsId) { disableLoadingData(); return; }
    getBacktestingResultById(backtestingResultsId)
      .then((res) => {
        if (res.status === 200) {
          let symbols = [...res.data.symbols];
          symbols.sort(sortByProfitFactor);

          setBacktestingResults({...res.data, symbols});
          setVisibleSymbol(symbols[0]);
          
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not get backtestingResults." });
        history.push("/backtesting-results");
      });
  }, [backtestingResultsId, disableLoadingData, history]);

  const symbolsColumns = [
    {
      dataField: "symbol",
      text: "coin",
      // formatter: (cell) => {
      //   if (cell === visibleSymbol.symbol) 
      //     return (<span style={{ color: fuxiaColor, fontWeight: 'bold' }}>{cell}</span>)
      //   else return (<span>{cell}</span>)
      // }
    },
    {
      dataField: "profitFactor",
      text: "Profit factor",
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) => `${(cell || 0).toFixed(2)}`,
      sort: true
    },
    {
      dataField: "benefit",
      text: "Benefit",
      headerAlign: 'center',
      align: 'center',
      sort: true,
      sortFunc: (a, b, order) => {
        const numA = parseFloat(a) || 0;
        const numB = parseFloat(b) || 0;
        return order === 'asc' ? numA - numB : numB - numA;
      }
    },
    {
      dataField: "symbol",
      text: "",
      formatter: symbolButtonsFormatter,
      align: 'right',
      headerStyles: { width: '60px' }
    }
  ]

  function symbolButtonsFormatter(cell, row) {
    const elem = backtestingResults.symbols.find((x) => x.symbol === cell);

    return (
      <>
        <Tooltip title="View">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={() => {
              setVisibleSymbol(elem)
            }}
          >
            <Visibility />
          </Button>
        </Tooltip>
      </>
    )
  }

  function getData(result) {
    let data = [];

    for (let i = 0; i < result?.symbols?.length; ++i) {
      const actS = result.symbols[i];
      data.push({
        ...actS, 
        benefit: (actS.accountAmount - result.config.accountAmount || 0).toFixed(2)
      });
    }

    return data;
  }

  const Graphic = () => {

    const ColText = ({text}) => {
      return (
        <Col>
          <p>
            {text}
          </p>
        </Col>
      )
    }

    let data = [{
      type: 'scatter',
      mode: 'lines+markers',
      marker: { color: 'blue' },
      x: [],
      y: []
    }];

    for (let i = 0; i < visibleSymbol?.historicAmount?.length; ++i) {
      const actS = visibleSymbol.historicAmount[i];
      data[0].x.push(i+1)
      data[0].y.push(actS);
    }

    return (
      <>
        <Row>
          <ColText text={`Coin: ${visibleSymbol.symbol}`}/>
          <ColText text={`Benefit: ${(visibleSymbol.accountAmount - backtestingResults.config.accountAmount || 0).toFixed(2)} USD`}/>
          <ColText text={`Total trades: ${visibleSymbol.totalTrades}`}/>
          <ColText text={`Profitable trades: ${((visibleSymbol.profitableTrades / visibleSymbol.totalTrades || 0) * 100).toFixed(2)}%`}/>
          <ColText text={`Profit factor: ${(visibleSymbol.profitFactor || 0).toFixed(2)}`}/>
        </Row>

        <Plot
          style={{ width: '70%', display: "flex" }}
          data={data}
          layout={{
              width: 800,
              height: 400,
              xaxis: {
                  showgrid: false,
                  title: null
              },
              yaxis: {
                  title: "",
                  showgrid: true,
              },
              legend: {
                  orientation: "h",
                  x: 0.5,
                  xanchor: "center",
                  y: -0.3,
              },
          }}
          config={{
          staticPlot: false,
          displayModeBar: false,
          scrollZoom: false,
          doubleClick: false,
          displaylogo: false,
          }}
        />
      </>
    )
  }

  const ConfigDialog = () => {
    return (
      <Dialog
        open={openConfigDialog}
        onClose={() => setOpenConfigDialog(false)}
        fullWidth
      >
        <DialogTitle>Configuration</DialogTitle>
        <DialogContent>
          <EditBacktestingConfigPageInfo 
              backtestingConfig={backtestingResults.config}
              setBacktestingConfig={() => {}}
              allCoins={[]}
              handleChange={() => {}}
              handleFormChange={() => {}}
              readonly={true}
          />
        </DialogContent>
      </Dialog>
    )
  }

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <MuiThemeProvider theme={theme}>
        <Card>
          <CardHeader title={`De la configuración ${backtestingResults?.config?.fullName || "Inexistente"} a las ${dateFormatter(backtestingResults?.finishedAt)}`}>
            {/* <p>HOLA</p> */}
            <MuiThemeProvider theme={theme}>
              <Button
                style={{ height: 40, alignSelf: 'center' }}
                onClick={() => {
                  setOpenConfigDialog(true);
                }}
                variant="outlined"
                color="primary"
              >
                Show config
              </Button>
            </MuiThemeProvider>
          </CardHeader>
          <CardBody>

            <Row>
              <Col>
                <Graphic />
              </Col>

              <Col>
              {!refresh && <Table data={getData(backtestingResults)} columns={symbolsColumns}/>}
              </Col>
            </Row>

            <ConfigDialog />
          </CardBody>
        </Card>
        <Button
          onClick={() => history.push("/backtesting-results")}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Back
        </Button>
        {/* <Button
          onClick={() => saveBacktestingResults()}
          variant="outlined"
          color="primary"
          style={{ marginRight: "20px" }}
        >
          Save backtestingResults
        </Button> */}
        {backtestingResultsId && !backtestingResults?.isMain && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Delete backtestingResults
              </Button>
            </MuiThemeProvider>

            <ConfirmDialog
              title={"Are you sure you want to delete this backtestingResults?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteBacktestingResult(backtestingResultsId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "BacktestingResults deleted successfully",
                      });
                      history.push("/backtesting-results");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete backtestingResults.",
                    });
                  });
              }}
            />
          </>
        )}
      </MuiThemeProvider>
    );
}
